import React from 'react';
import { Spinner } from '@/components/ui/spinner';
import PdfMinimap from './pdf/PdfMinimap';
import PdfPageSurface from './pdf/PdfPageSurface';
import PdfResultsSidebar from './pdf/PdfResultsSidebar';
import PdfSearchBar from './pdf/PdfSearchBar';
import PdfToolbar from './pdf/PdfToolbar';
import type { PdfDisplayMode, SearchResult } from './pdf/types';
import { useHighlightLayers } from './pdf/useHighlightLayers';
import { useInitialPageSync, usePdfDocument } from './pdf/usePdfDocument';
import { usePdfNavigation } from './pdf/usePdfNavigation';
import { usePdfPageRender } from './pdf/usePdfPageRender';
import { usePdfScroll } from './pdf/usePdfScroll';
import { usePdfSearch } from './pdf/usePdfSearch';
import { usePdfViewerState } from './pdf/usePdfViewerState';

export type { SearchResult } from './pdf/types';

interface PDFHighlightViewerProps {
  pdfUrl: string;
  highlightTerms?: string[];
  initialPage?: number;
  initialPageExplicit?: boolean;
  preferredSearchPage?: number;
  onSearchResults?: (results: SearchResult[]) => void;
  /** 'single' renders page by page, 'scroll' renders a continuous document. */
  displayMode?: PdfDisplayMode;
  /** Pages preloaded before/after the visible ones in scroll mode. */
  lazyLoadBuffer?: number;
}

const PDFHighlightViewer: React.FC<PDFHighlightViewerProps> = ({
  pdfUrl,
  highlightTerms = [],
  initialPage = 1,
  initialPageExplicit = false,
  preferredSearchPage,
  onSearchResults,
  displayMode = 'scroll',
  lazyLoadBuffer = 2,
}) => {
  const state = usePdfViewerState({
    initialPage,
    initialPageExplicit,
    preferredSearchPage,
    highlightTerms,
  });
  const { scrollToPage, scrollToMatch } = usePdfScroll(state);

  // Effect order is load → navigate → render → highlight → search → shortcuts;
  // the highlights depend on the page canvases already existing.
  usePdfDocument(pdfUrl, state);
  useInitialPageSync({
    state, initialPage, initialPageExplicit, preferredSearchPage, displayMode, scrollToPage,
  });
  usePdfPageRender({ state, displayMode, lazyLoadBuffer, initialPage, scrollToPage });
  useHighlightLayers({ state, displayMode, scrollToMatch });
  const { performSearch } = usePdfSearch({
    state, pdfUrl, displayMode, highlightTerms, onSearchResults, scrollToMatch,
  });
  const {
    handlePrevPage, handleNextPage, handleZoomIn, handleZoomOut,
    handlePrevResult, handleNextResult, handleResultClick,
  } = usePdfNavigation({ state, displayMode, scrollToPage });

  const {
    currentPage, totalPages, scale, loading, error, searchQuery, searchResults,
    currentResultIndex, showSearchBar, showResultsSidebar,
    setSearchQuery, setShowSearchBar, setShowResultsSidebar, containerRef,
  } = state;

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch(searchQuery);
  };

  if (loading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <p className="revy-cjk text-sm leading-[1.5] text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="relative flex h-full flex-col">
      <PdfToolbar
        currentPage={currentPage}
        totalPages={totalPages}
        scale={scale}
        hasResults={searchResults.length > 0}
        showResultsSidebar={showResultsSidebar}
        onPrevPage={handlePrevPage}
        onNextPage={handleNextPage}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onToggleSearch={() => setShowSearchBar(!showSearchBar)}
        onToggleResults={() => setShowResultsSidebar(!showResultsSidebar)}
      />

      {showSearchBar && (
        <PdfSearchBar
          query={searchQuery}
          resultCount={searchResults.length}
          currentResultIndex={currentResultIndex}
          onQueryChange={setSearchQuery}
          onSubmit={handleSearchSubmit}
          onPrevResult={handlePrevResult}
          onNextResult={handleNextResult}
          onClose={() => setShowSearchBar(false)}
        />
      )}

      <div className="relative flex min-h-[500px] flex-1">
        <PdfPageSurface ref={containerRef} />

        {searchResults.length > 0 && totalPages > 0 && (
          <PdfMinimap
            results={searchResults}
            totalPages={totalPages}
            currentPage={currentPage}
            currentResultIndex={currentResultIndex}
            onSelectResult={handleResultClick}
          />
        )}
      </div>

      {showResultsSidebar && (
        <PdfResultsSidebar
          results={searchResults}
          currentResultIndex={currentResultIndex}
          onSelectResult={handleResultClick}
          onPrevResult={handlePrevResult}
          onNextResult={handleNextResult}
          onClose={() => setShowResultsSidebar(false)}
        />
      )}
    </div>
  );
};

export default PDFHighlightViewer;
